// Styles for the global duel-social layer: the "Friends online" floating button + drawer and the
// incoming-invite toast (docked to the right, where the drawer lives). Prefix: dso-. Glass tokens with
// literal fallbacks so it renders even if a token is absent. Registered in STYLING.md. Action buttons
// use the library Button components (no hand-rolled button styles here).
export const duelSocialStyles = `
/* Icon-only round button: the label made a wide pill in a corner it shares with the draggable
   mascot. 52px keeps the tap target well above the 44px floor at every width. The name is carried
   by aria-label + title, and by the drawer heading once it is open. */
.dso-fab {
  position: fixed;
  right: var(--space-4, 16px);
  bottom: var(--space-6, 24px);
  z-index: var(--z-sticky, 900);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.4));
  border-radius: var(--radius-full, 999px);
  background: var(--accent, #57c2d3);
  color: #ffffff;
  cursor: pointer;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.18));
  transition: transform var(--duration-fast, 150ms) var(--ease-out, ease);
}
.dso-fab:hover { transform: translateY(-2px); }

.dso-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal, 1000);
  background: rgba(15, 30, 40, 0.35);
  display: flex;
  justify-content: flex-end;
}
.dso-drawer {
  width: min(420px, 100%);
  height: 100%;
  background: var(--glass-bg, rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-left: 1px solid var(--glass-border, rgba(255, 255, 255, 0.4));
  box-shadow: var(--shadow-xl, -12px 0 32px rgba(0, 0, 0, 0.2));
  display: flex;
  flex-direction: column;
}
.dso-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4, 16px);
  border-bottom: 1px solid var(--glass-border, rgba(255, 255, 255, 0.35));
}
.dso-title { font-size: var(--fs-18, 18px); font-weight: 700; color: var(--catchup-dark-blue, #0f3b4a); }
.dso-close {
  background: none;
  border: none;
  font-size: var(--fs-18, 18px);
  cursor: pointer;
  color: var(--catchup-gray, #799ba6);
  padding: var(--space-1, 4px);
}
.dso-toolbar {
  display: flex;
  gap: var(--space-2, 8px);
  align-items: center;
  padding: var(--space-3, 12px) var(--space-4, 16px);
}
.dso-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: var(--space-2, 8px) var(--space-3, 12px);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.5));
  border-radius: var(--radius-md, 12px);
  background: var(--glass-bg-soft, rgba(255, 255, 255, 0.6));
}
.dso-search input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: var(--fs-14, 14px);
  color: var(--catchup-dark-blue, #0f3b4a);
}
.dso-refresh {
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.5));
  border-radius: var(--radius-md, 12px);
  background: var(--glass-bg-soft, rgba(255, 255, 255, 0.6));
  cursor: pointer;
  padding: var(--space-2, 8px) var(--space-3, 12px);
  font-size: var(--fs-14, 14px);
  color: var(--catchup-dark-blue, #0f3b4a);
}
.dso-list {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-2, 8px) var(--space-4, 16px) var(--space-4, 16px);
}
.dso-row {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  padding: var(--space-2, 8px) var(--space-3, 12px);
  border-radius: var(--radius-md, 12px);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.3));
  background: var(--glass-bg-soft, rgba(255, 255, 255, 0.55));
  margin-bottom: var(--space-2, 8px);
}
.dso-row-ava {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  background: var(--glass-bg-soft, rgba(87, 194, 211, 0.12));
}
.dso-row-ava img { max-width: 100%; max-height: 100%; }
.dso-row-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.dso-row-name { font-size: var(--fs-14, 14px); font-weight: 600; color: var(--catchup-dark-blue, #0f3b4a); }
.dso-row-sub { font-size: var(--fs-12, 12px); color: var(--catchup-gray, #799ba6); }
.dso-row-actions { display: flex; gap: var(--space-2, 8px); flex-shrink: 0; }
.dso-state { text-align: center; color: var(--catchup-gray, #799ba6); padding: var(--space-6, 24px); font-size: var(--fs-14, 14px); }
.dso-empty { padding: var(--space-6, 24px) var(--space-4, 16px); }

.dso-toast {
  position: fixed;
  top: var(--space-4, 16px);
  right: var(--space-4, 16px);
  z-index: var(--z-toast, 9998);
  width: min(380px, calc(100% - 32px));
  padding: var(--space-4, 16px) var(--space-4, 16px) var(--space-5, 20px);
  border-radius: var(--radius-xl, 22px);
  border: 1px solid rgba(120, 180, 200, 0.5);
  background:
    radial-gradient(130% 90% at 100% 0%, rgba(87, 194, 211, 0.14), transparent 58%),
    #ffffff;
  box-shadow: 0 18px 44px rgba(15, 59, 74, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  animation: dso-toast-in var(--duration-normal, 260ms) var(--ease-out, cubic-bezier(0.22, 1, 0.36, 1));
}
.dso-toast--urgent {
  box-shadow: 0 18px 44px rgba(255, 107, 107, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
@keyframes dso-toast-in {
  from { opacity: 0; transform: translateX(28px) scale(0.98); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}
.dso-toast-main { display: flex; align-items: center; gap: var(--space-4, 16px); }
.dso-medallion { position: relative; width: 60px; height: 60px; flex-shrink: 0; }
.dso-ring { position: absolute; inset: 0; width: 60px; height: 60px; transform: rotate(-90deg); }
.dso-ring-track { fill: none; stroke: rgba(120, 180, 200, 0.22); stroke-width: 4; }
.dso-ring-prog {
  fill: none;
  stroke: var(--accent, #57c2d3);
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.25s linear, stroke 0.3s ease;
}
.dso-toast--urgent .dso-ring-prog { stroke: var(--catchup-coral, #ff6b6b); }
.dso-medallion-core {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(150deg, var(--accent, #57c2d3), var(--accent-strong, #3aa9bb));
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.5);
  animation: dso-medallion-pulse 1.8s ease-in-out infinite;
}
@keyframes dso-medallion-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
.dso-medallion-emoji { font-size: 24px; line-height: 1; }
.dso-toast-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: var(--space-1, 4px); }
.dso-toast-kicker {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  font-size: var(--fs-11, 11px);
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-strong, #3aa9bb);
}
.dso-toast-secs {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--accent-soft, rgba(87, 194, 211, 0.16));
  color: var(--accent-strong, #3aa9bb);
  font-variant-numeric: tabular-nums;
}
.dso-toast--urgent .dso-toast-secs {
  background: rgba(255, 107, 107, 0.14);
  color: var(--catchup-coral, #ff6b6b);
}
.dso-toast-more {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--catchup-dark-blue, #0f3b4a);
  color: #ffffff;
  font-variant-numeric: tabular-nums;
}
.dso-toast-msg { font-size: var(--fs-14, 14px); color: var(--catchup-dark-blue, #0f3b4a); margin: 0; line-height: 1.35; }
.dso-toast-msg b { font-weight: 800; }
.dso-toast-actions {
  display: flex;
  gap: var(--space-2, 8px);
  justify-content: flex-end;
  align-items: center;
  margin-top: var(--space-4, 16px);
}

@media (max-width: 480px) {
  .dso-drawer { width: 100%; }
  .dso-toast { left: var(--space-4, 16px); width: auto; }
}
`;

// Appended: the friends drawer's tab bar, presence chips and request badges.
export const friendPanelStyles = `
.dso-tabs {
  display: flex;
  gap: var(--space-1, 4px);
  padding: var(--space-2, 8px) var(--space-4, 16px) 0;
  border-bottom: 1px solid var(--glass-border, rgba(255, 255, 255, 0.35));
}
.dso-tab {
  position: relative;
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2, 8px);
  padding: var(--space-2, 8px) var(--space-2, 8px) var(--space-3, 12px);
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  cursor: pointer;
  font-size: var(--fs-13, 13px);
  font-weight: 600;
  color: var(--catchup-gray, #799ba6);
  white-space: nowrap;
}
.dso-tab-on {
  color: var(--catchup-dark-blue, #0f3b4a);
  border-bottom-color: var(--accent, #57c2d3);
}
.dso-tab-badge,
.dso-fab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--radius-full, 999px);
  background: var(--catchup-coral, #f2726f);
  color: #ffffff;
  font-size: var(--fs-11, 11px);
  font-weight: 700;
  line-height: 1;
}
/* Pending-request count, docked to the button's top-right corner now that there is no label to sit
   beside. The ring separates it from the accent fill it overlaps. */
.dso-fab-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  box-shadow: 0 0 0 2px var(--accent, #57c2d3);
}

.dso-sec-label {
  padding: var(--space-3, 12px) var(--space-1, 4px) var(--space-2, 8px);
  font-size: var(--fs-11, 11px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--catchup-gray, #799ba6);
}

/* An offline friend stays listed — dimmed, not hidden, so the list is a stable roster. */
.dso-row-offline { opacity: 0.62; }

.dso-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  padding: 2px 8px 2px 6px;
  width: fit-content;
  border-radius: var(--radius-full, 999px);
  font-size: var(--fs-11, 11px);
  font-weight: 600;
  background: rgba(121, 155, 166, 0.14);
  color: var(--catchup-gray, #799ba6);
}
.dso-chip-online { background: rgba(76, 175, 122, 0.16); color: #2f7d55; }
.dso-chip-solving { background: rgba(87, 194, 211, 0.18); color: #1c7c8c; }
.dso-chip-in-duel { background: rgba(242, 114, 111, 0.16); color: #b8433f; }
.dso-chip-in-exam { background: rgba(240, 173, 78, 0.2); color: #96631a; }

.dso-row-note { font-size: var(--fs-12, 12px); color: var(--catchup-gray, #799ba6); }
.dso-row-hint { display: inline-flex; }
.dso-row-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-md, 12px);
  background: transparent;
  cursor: pointer;
  color: var(--catchup-gray, #799ba6);
}
.dso-row-more:hover { background: rgba(242, 114, 111, 0.12); }

.dso-confirm { display: flex; flex-direction: column; gap: var(--space-4, 16px); }
.dso-confirm-text {
  font-size: var(--fs-14, 14px);
  color: var(--catchup-dark-blue, #0f3b4a);
  margin: 0;
}
.dso-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2, 8px);
  flex-wrap: wrap;
}

/* The drawer is full-width on a phone; the tab labels have to survive that. */
@media (max-width: 480px) {
  .dso-drawer { width: 100%; }
  .dso-tab { font-size: var(--fs-12, 12px); }
  .dso-row-actions { flex-direction: column; align-items: stretch; }
}
`;
